import { bufferToHexString } from '@iotize/common/byte-converter';
import { CodeError } from '@iotize/common/error';
import {
  ApduResponse,
  TapRequestFrame,
  TapResponseFrame,
} from '@iotize/tap/client/api';

import { TapRequestHelper } from './request/tap-request-helper';
import { ResultCodeTranslation } from './response/result-code-translation';

export class TapClientError extends CodeError<TapClientError.Code> {
  constructor(
    code: TapClientError.Code,
    message: string,
    public cause?: Error,
  ) {
    super(message, code);
  }

  public static illegalArgument(msg: string): TapClientError {
    return new TapClientError(TapClientError.Code.IllegalArgumentError, msg);
  }

  public static illegalStateError(msg: string) {
    return new TapClientError(TapClientError.Code.IllegalStateError, msg);
  }

  public static encodeRequestError(
    command: TapRequestFrame,
    cause: Error,
  ): TapClientError {
    return new TapClientError(
      TapClientError.Code.EncodeRequestError,
      'Cannot encode this request: ' + command + '. Cause: ' + cause,
      cause,
    );
  }

  public static decodeResponseError(
    cause: Error,
    frameOrCommand: Uint8Array | TapRequestFrame,
  ) {
    return new TapClientError(
      TapClientError.Code.DecodeResponseError,
      `Cannot decode response to command ${
        frameOrCommand instanceof Uint8Array
          ? `0x${bufferToHexString(frameOrCommand)}`
          : TapRequestHelper.toString(frameOrCommand)
      }. Cause: ` + cause,
      cause,
    );
  }

  public static notConnectedError(): TapClientError {
    return new TapClientError(
      TapClientError.Code.NotConnectedError,
      'Trying to execute command but device is not connected',
    );
  }

  static cannotDecodeResponseError(err: Error, bufferData: Uint8Array) {
    return new TapClientError(
      TapClientError.Code.UnexpectedTapResponse,
      `Received from device an unexpected response that cannot be decoded (${
        err.message
      }). Frame: 0x${bufferToHexString(bufferData)}`,
    );
  }

  static unexpectedApduStatusCode(apduResponse: ApduResponse) {
    return new TapClientError(
      TapClientError.Code.UnexpectedApduResponseStatus,
      `Received from device an unexpected response with an invalid APDU status code 0x${apduResponse.status.toString(
        16,
      )}
            but expecting 0x${ApduResponse.Status.OK.toString(16)}`,
    );
  }

  static cannotEncodeRequest(err: Error, tapRequest: TapRequestFrame) {
    return new TapClientError(
      TapClientError.Code.CannotEncodeRequest,
      `Cannot encode request ${tapRequest}: ${err.message}`,
    );
  }
}

export namespace TapClientError {
  export enum Code {
    NotConnectedError = 'TapClientErrorNotConnected',
    EncodeRequestError = 'TapClientErrorEncodeRequest',
    IllegalArgumentError = 'TapClientErrorIllegalArgument',
    IllegalStateError = 'TapClientErrorIllegalState',
    DecodeResponseError = 'TapClientErrorDecodeResponse',
    UnexpectedTapResponse = 'TapClientErrorUnexpectedTapResponse',
    UnexpectedApduResponseStatus = 'TapClientErrorUnexpectedApduResponseStatus',
    CannotEncodeRequest = 'TapClientErrorCannotEncodeRequest',
    TapClientResponseStatusError = 'TapClientErrorResponseStatus',
  }
}

// export const TapResponseErrorCode = 'TapResponseError';

export class TapClientResponseStatusError extends TapClientError {
  // public code: string;

  constructor(
    public response: TapResponseFrame,
    public request?: TapRequestFrame,
  ) {
    super(
      TapClientError.Code.TapClientResponseStatusError,
      TapClientResponseStatusError.createErrorMessage(response, request),
    );
  }

  public static createErrorMessage(
    response: TapResponseFrame,
    request?: TapRequestFrame,
  ): string {
    const codeRet = response.status;
    let msg = 'Tap request failed. ';
    if (request) {
      msg = `Tap request ${TapRequestHelper.toString(request)} failed. `;
    }
    const errorExplained =
      codeRet in ResultCodeTranslation
        ? ResultCodeTranslation[codeRet as keyof typeof ResultCodeTranslation]
        : 'an unknown error';
    msg += `${errorExplained} (code=0x${codeRet.toString(16)})`;
    return msg;
  }
}
